<script>
import {defineComponent, reactive} from "vue";
import 'vue3-carousel/dist/carousel.css';
import {Carousel, Slide, Navigation, Pagination} from 'vue3-carousel';
import CampaignCard from "@/javascript/views/components/landing/CampaignCard.vue";
import {useRoute, useRouter} from "vue-router";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import {useLandingPageStore} from "../../../stores/landingPage";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";
import tinycolor from "tinycolor2";

export default defineComponent({
  name: "DonatableTargetsList",
  components: {
    Pagination,
    Carousel,
    Slide,
    Navigation,
    CampaignCard
  },
  props: {
    /**
     * The layout of the campaigns section.
     * Currently only one exists.
     */
    layout: {
      type: String,
      default: 'list',
      validator: (value) => {
        return ['carousel', 'grid', 'list'].includes(value);
      }
    },
    // props used in all layouts
    /**
     * Title of the campaigns section.
     */
    title: {
      type: String,
      default: 'Ways to Give'
    },
    /**
     * Short Description of the campaigns section.
     */
    short_description: {
      type: String,
      default: ''
    },
    /**
     * Description of the campaigns section.
     */
    description: {
      type: String,
      default: ''
    },
    /**
     * Org object, needed for donation form / donate button.
     */
    organization: {
      type: Object,
      default: () => ({})
    },
    /**
     * Array of campaign objects.
     */
    donationTargets: {
      type: Array,
      default: () => []
    },
    /**
     * Custom sorting order
     */
    order: {
      type: Array,
      required: false
    },
    /**
     * Number of items to show on the carousel.
     */
    itemsToShow: {
      type: Number,
      default: 3.5
    },
    /**
     * Number of items to scroll when next/prev buttons on carousel are clicked.
     */
    itemsToScroll: {
      type: Number,
      default: 1
    },
    /**
     * Will disable infinite scroll if true.
     */
    disableInfiniteScroll: {
      type: Boolean,
      default: false
    },
    /**
     * How the cards should be aligned in the carousel.
     */
    snapAlign: {
      type: String,
      default: 'starts',
      validator: (value) => {
        return ['start', 'end', 'center'].includes(value);
      }
    },
    /**
     * Background color of campaigns section.
     * Can be any valid css color syntax.
     */
    backgroundColor: {
      type: String,
      default: '#f3f2f2' // $color-section-bg
    },
    /**
     * Text color for the campaign section title/description.
     */
    textColor: {
      type: String,
      default: '#000000'
    },
    // card props
    /**
     * Whether the image in the card should be located on the top or the bottom.
     */
    cardImagePosition: {
      type: String,
      default: 'top',
      validator: (value) => {
        return ['top', 'bottom'].includes(value);
      }
    },
    /**
     * Background color of the campaign cards.
     * Can be any valid css color syntax.
     */
    cardBackgroundColor: {
      type: String,
      default: 'white'
    },
    /**
     * Primary color for donate bar, buttons, etc. Simplifying Props
     */
    primaryColor: {
      type: String,
      default: '#085E2DFF'
    },
    primaryDarkColor: {
      type: String,
      default: '#104205FF'
    },
    /**
     * Color used on top of primary color - ensure enough contrast!
     */
    primaryTextColor: {
      type: String,
      default: 'white'
    },

    neutralColor: {
      type: String,
      default: '#F3F2F2' // $color-section-bg
    },
    /**
     * Background color of donate button to be used on hover.
     */
    donateButtonHoverBackgroundColor: {
      type: String,
      default: '#082103FF'
    },
    /**
     * Text color of donate button to be used on hover.
     */
    donateButtonTextColor: {
      type: String,
      default: '#FFFFFF'
    },
    /**
     * Disables the onClick for a campaign Card
     */
    disableClick: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    props = reactive(props);
    const router = useRouter();
    const route = useRoute();
    const analyticEventStore = useAnalyticEventStore();
    const landingStore = useLandingPageStore();
    return {
      router: router,
      route: route,
      analyticEventStore: analyticEventStore,
      landingStore
    };
  },
  data() {
    return {
      carouselConfig: {
        itemsToShow: this.itemsToShow,
        itemsToScroll: this.itemsToScroll,
        snapAlign: this.snapAlign,
        wrapAround: !this.disableInfiniteScroll,
        dir: 'ltr',
        landingPage: false,
        breakpointMode: "carousel", // Ensure queries are based on container and not screen size
        // Carousel breakpoints
        breakpoints: {
          0: {
            itemsToShow: 1,
            gap: 16
          },
          720: {
            itemsToShow: 1,
            gap: 20
          },
          960: {
            itemsToShow: 1.3,
            gap: 24
          },
        },
      },

      selectedCampaign: null
    };
  },

  computed: {
    classes() {
      return {
        'campaigns-content-container': true,
        [`layout--${this.layout || 'grid'}`]: true,
      };
    },
    cssProps() {
      // check for contrast on passed colour for paginator
      let buttonColour = this.primaryColor;
      let fallbackGray = "#999";

      const colorObj = tinycolor(this.primaryColor);
      if (!colorObj.isValid()) {
        buttonColour = fallbackGray;
      }
      // Check for luminance / visibility
      const luminance = colorObj.getLuminance();
      if(luminance > 0.8) {
        buttonColour = fallbackGray;
      }
      const buttonColorDark = tinycolor(buttonColour).darken(20).toHexString();

      return {
        '--section-bg': tinycolor(this.primaryColor).setAlpha(0.2).toRgbString(),
        '--text-color': this.textColor,
        '--carousel-btn-color': buttonColour,
        '--carousel-btn-color--dark': buttonColorDark,
      };
    },
    sortedTargets() {
      if (this.order && this.order.length > 0) {
        return ApplicationHelper.sortCampaignsByCustomOrder(this.donationTargets, this.order);
      } else {
        return this.donationTargets;
      }
    },
    loading() {
      return this.donationTargets.length === 0 || this.landingStore.loading;
    },
  },
  methods: {
    navigateToShow(donationTarget) {
      if (!this.disableClick) {
        this.analyticEventStore.setOrganizationId(this.organization.id);
        this.analyticEventStore.logEvent({
          action: `landing-navigate-to-show-${donationTarget.model.toLowerCase()}`,
          traceable_type: donationTarget.model,
          traceable_id: donationTarget.id,
          metadata: {
            donationTarget
          }
        });
        console.log('target', donationTarget);
        this.router.push({
          name: "donatable-show",
          params: {
            ...this.route.params,
            slug: donationTarget.slug,
          }
        });
      }
    },
  }
});

</script>

<template>
  <div
    id="landing-campaigns"
    class="outer-container"
    :style="cssProps"
  >
    <div
      class="inner-container"
    >
      <div :class="classes">
        <template v-if="donationTargets && !loading">
          <div
            v-if="title || description"
            class="campaign-section-text"
          >
            <h2
              v-if="title"
              class="section-title"
            >
              {{ title }}
            </h2>

            <p
              v-if="description"
              class="section-subtitle"
            >
              {{ description }}
            </p>
          </div>
          <div
            v-if="layout === 'carousel'"
            class="carousel__wrapper"
          >
            <carousel
              v-if="layout === 'carousel'"
              v-bind="carouselConfig"
            >
              <slide
                v-for="(campaign, i) in sortedTargets"
                :key="i"
              >
                <CampaignCard
                  :campaign="campaign"
                  image-alt="Image alt text"
                  show-description
                  image-position="left"
                  card-height="large"
                  :primary-color="primaryColor"
                  :primary-color-dark="primaryDarkColor"
                  :primary-text-color="primaryTextColor"
                  :background-color="cardBackgroundColor"
                  donate-button
                  clickable
                  show-learn-more-button
                  @click="navigateToShow(campaign)"
                />
              </slide>

              <template #addons>
                <navigation />
                <Pagination
                  paginate-by-items-to-show
                />
              </template>
            </carousel>
          </div>


          <div v-else-if="layout.includes('grid') || layout.includes('list')">
            <div class="campaigns-grid">
              <CampaignCard
                v-for="(campaign, i) in sortedTargets"
                :key="i"
                :campaign="campaign"
                show-description
                :image-position="layout.includes('grid') ? 'top' : 'left'"
                card-height="medium"
                :primary-color="primaryColor"
                :primary-color-dark="primaryDarkColor"
                :primary-text-color="primaryTextColor"
                :background-color="cardBackgroundColor"
                :clickable="!disableClick"
                show-learn-more-button
                @click="navigateToShow(campaign)"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <!-- Skeleton loader-->
          <carousel
            v-if="layout === 'carousel'"
            v-bind="carouselConfig"
          >
            <slide
              v-for="(i) in 9"
              :key="i"
            >
              <div class="campaign-skeleton" />
            </slide>

            <template #addons>
              <navigation />
            </template>
          </carousel>

          <div
            v-else
            class="campaigns-grid"
          >
            <div
              v-for="i in 9"
              class="campaign-skeleton"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#landing-campaigns {
  background-color: var(--section-bg);
  padding: $space-3xl 0;

  .inner-container {
    padding: 0 !important;
  }

  .campaign-skeleton {
    min-height: 200px;
    @apply bg-gray-300 animate-pulse;
    border-radius: $radius-rounded;
  }

  .campaigns-content-container {
    padding: 0 $space-md;

    .campaign-section-text {
      margin-bottom: $space-2xl;
      color: var(--text-color);
      text-align: center;

      .section-title {
        font-size: $section-title-size;
        font-weight: $section-title-weight;
        line-height: $section-title-line;
        margin-bottom: $space-xs;
        color: $color-neutral-900;
      }

      .section-subtitle {
        font-size: $section-body-size;
        font-weight: $section-body-weight;
        line-height: $section-body-line;
        color: $section-body-color;
        margin-top: $space-xs;
      }
    }

    /** Carouse styling*/

    .carousel {
      margin-bottom: $space-2xl;

      // CSS variable overrides for paginator
      --vc-pgn-background-color: var(--carousel-btn-color);
      --vc-pgn-active-color: var(--carousel-btn-color--dark);
      --vc-pgn-border-radius: 5px;
      --vc-pgn-height: 7px;
      --vc-pgn-width: 7px;
      --vc-pgn-gap: 15px;
      --vc-png-bottom: -35px;
    }

    :deep(.carousel__track) {
      align-items: stretch;

      .carousel__slide {
        display: flex;
        align-items: stretch;
      }
    }

    :deep(.carousel__prev) {
      margin: $space-none;
      left: -42px;
    }

    :deep(.carousel__next) {
      margin: $space-none;
      right: -42px;
    }
  }

}

.layout--list {
  .campaigns-grid {
    @apply grid grid-cols-1;
    gap: $space-xl;
    max-width: $breakpoint-md;
    margin: 0 auto;
  }
}

.layout--grid, .layout--gridSmall {
  .campaigns-grid {
    @apply grid grid-cols-1;
    gap: $space-lg;
  }

  // Media queries (live site)
  @media (min-width: 480px) {
    .campaigns-grid {
      @apply grid-cols-2;
      gap: $space-xl;
    }
  }

  @screen md {
    .campaigns-grid {
      @apply grid-cols-3;
      gap: $space-xl;
    }
  }

  @screen lg {
    .campaigns-grid {
      @apply grid-cols-4;
      gap: $space-xl;
    }
  }

  // Container queries (builder preview)
  @container (min-width: 0px) and (max-width: 479px) {
    .campaigns-grid {
      @apply grid-cols-1;
      gap: $space-lg;
    }
  }

  @container (min-width: 480px) and (max-width: 767px) {
    .campaigns-grid {
      @apply grid-cols-2;
      gap: $space-xl;
    }
  }

  @container (min-width: 768px) and (max-width: 1023px) {
    .campaigns-grid {
      @apply grid-cols-3;
      gap: $space-xl;
    }
  }

  @container (min-width: 1024px) {
    .campaigns-grid {
      @apply grid-cols-4;
      gap: $space-xl;
    }
  }
}

.layout--grid, .layout--gridSmall, .layout--carousel {
  .campaign-skeleton {
    height: 300px;
    @apply bg-gray-300 animate-pulse;
    border-radius: $radius-rounded;
    @apply w-full;
  }
}


</style>
